// Amari's two plans, free and Plus: what each includes, and the rules the app checks.
//
// The server decides who has Plus (snapcook_entitlements) and enforces what costs money (scans,
// Ask Amari). Everything here is the app's side: what to show, and the weekly recipe allowance.

import { mondayOf } from './progress';
import type { Goal } from './preferences';

export type Membership = 'free' | 'plus';

/** Full recipes (method and cook mode) a free account can open each week, Monday to Sunday. */
export const FREE_RECIPES_PER_WEEK = 4;
/** Photo scans per day; the server enforces these (snapcook_config). */
export const FREE_SCANS_PER_DAY = 3;

/** Shown on the Plus page. There is no purchase yet; the page collects interest. */
export const PLUS_PRICE = { monthly: '$7.99', yearly: '$59.99', yearlySaving: '37%', yearlyPerMonth: '$5.00' } as const;

/** What Plus adds, in the order the Plus page lists it. */
export const PLUS_FEATURES: { title: string; detail: string }[] = [
  { title: 'Ask Amari', detail: 'Your AI cooking copilot: what to make, swaps, and plans, from your own kitchen' },
  { title: 'Every recipe, every week', detail: 'Open and cook as many recipes as you like' },
  { title: 'Unlimited fridge scans', detail: 'Scan whenever you shop or cook' },
  { title: 'Calories and macros', detail: 'Calories, protein, carbs and fat on every meal' },
  { title: 'Fitness goals', detail: 'Build muscle, lose or gain weight, maintain, or train for sport' },
  { title: 'Weekly autopilot', detail: 'Amari plans your whole week in one tap' },
];

/** Goals about body weight and training, which are part of Plus. Health goals stay free. */
export const FITNESS_GOALS: readonly Goal[] = ['maintain', 'gain', 'lose', 'muscle', 'performance'];

export const isFitnessGoal = (g: Goal) => FITNESS_GOALS.includes(g);

/** The goals that apply: a free account keeps the goals it chose, but fitness ones only count with Plus. */
export function effectiveGoals(goals: Goal[], membership: Membership): Goal[] {
  return membership === 'plus' ? goals : goals.filter((g) => !isFitnessGoal(g));
}

/** Recipes opened in full this week. A new week starts empty. */
export interface RecipeUnlocks { week: string; ids: string[] }

export interface RecipeAccess {
  /** The method and cook mode can be shown. */
  open: boolean;
  /** Free recipes left to open this week (Infinity with Plus). */
  left: number;
}

export function recipeAccess(unlocks: RecipeUnlocks, recipeId: string, membership: Membership, today: string): RecipeAccess {
  if (membership === 'plus') return { open: true, left: Infinity };
  const ids = unlocks.week === mondayOf(today) ? unlocks.ids : [];
  return { open: ids.includes(recipeId), left: Math.max(0, FREE_RECIPES_PER_WEEK - ids.length) };
}

/** Opens a recipe for this week, or null when the week's allowance is used up. */
export function unlockRecipe(unlocks: RecipeUnlocks, recipeId: string, today: string): RecipeUnlocks | null {
  const week = mondayOf(today);
  const ids = unlocks.week === week ? unlocks.ids : [];
  if (ids.includes(recipeId)) return { week, ids };
  if (ids.length >= FREE_RECIPES_PER_WEEK) return null;
  return { week, ids: [...ids, recipeId] };
}

/** Two devices' unlocks: the same week combines, otherwise the newer week wins. */
export function mergeUnlocks(a: RecipeUnlocks, b: RecipeUnlocks): RecipeUnlocks {
  if (a.week === b.week) return { week: a.week, ids: [...new Set([...a.ids, ...b.ids])] };
  return a.week > b.week ? a : b;
}
